"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

export type OverlayId = "chat" | "locations" | "abstractions" | "notifications";

const STORAGE_KEY = "sidebar-pinned-overlay";

interface SidebarPinContextValue {
  pinnedOverlay: OverlayId | null;
  pinSidebar: (overlay: OverlayId | null) => void;
  togglePin: (overlay: OverlayId) => void;
}

const SidebarPinContext = createContext<SidebarPinContextValue>({
  pinnedOverlay: null,
  pinSidebar: () => {},
  togglePin: () => {},
});

export function SidebarPinProvider({ children }: { children: React.ReactNode }) {
  const [pinnedOverlay, setPinnedOverlay] = useState<OverlayId | null>(null);

  useEffect(() => {
    try {
      const legacyKey = "sidebar-locations-pinned";
      const legacy = localStorage.getItem(legacyKey);
      if (legacy === "true") {
        localStorage.setItem(STORAGE_KEY, "locations");
        localStorage.removeItem(legacyKey);
        setPinnedOverlay("locations");
        return;
      }
      if (legacy !== null) localStorage.removeItem(legacyKey);

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "chat" || stored === "locations" || stored === "abstractions" || stored === "notifications") {
        setPinnedOverlay(stored);
      }
    } catch {}
  }, []);

  const pinSidebar = useCallback((overlay: OverlayId | null) => {
    setPinnedOverlay(overlay);
    try {
      if (overlay) localStorage.setItem(STORAGE_KEY, overlay);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const togglePin = useCallback((overlay: OverlayId) => {
    setPinnedOverlay((prev) => {
      const next = prev === overlay ? null : overlay;
      try {
        if (next) localStorage.setItem(STORAGE_KEY, next);
        else localStorage.removeItem(STORAGE_KEY);
      } catch {}
      return next;
    });
  }, []);

  return (
    <SidebarPinContext.Provider value={{ pinnedOverlay, pinSidebar, togglePin }}>
      {children}
    </SidebarPinContext.Provider>
  );
}

export function useSidebarPin() {
  return useContext(SidebarPinContext);
}
